import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { MediaOfferingsTable } from "./media-dialog/MediaOfferingsTable";
import { MediaDialogFooter } from "./media-dialog/MediaDialogFooter";

interface ProviderMediaDialogProps {
  providerId: number;
  isOpen: boolean;
  onClose: () => void;
}

interface MediaOffering {
  media_id: number;
  media_name: string;
  media_descr: string | null;
  price: number;
  is_available: boolean;
  isOffered: boolean;
}

export function ProviderMediaDialog({ providerId, isOpen, onClose }: ProviderMediaDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [localMediaOfferings, setLocalMediaOfferings] = useState<MediaOffering[]>([]);
  const [inkCost, setInkCost] = useState<number>(0);
  const [certificateCost, setCertificateCost] = useState<number>(0);
  const [cutCost, setCutCost] = useState<number>(0);

  const { data: mediaOfferings, isLoading } = useQuery({
    queryKey: ["provider-media", providerId],
    queryFn: async () => {
      console.log("Fetching media offerings for provider:", providerId);
      
      if (!providerId) {
        console.log("No provider ID provided, skipping fetch");
        return [];
      }

      // Fetch provider details to get ink_cost, certificate_cost and cut_cost
      const { data: provider, error: providerError } = await supabase
        .from("print_service_providers")
        .select("ink_cost, certificate_cost, cut_cost")
        .eq("provider_id", providerId)
        .single();

      if (providerError) {
        console.error("Error fetching provider:", providerError);
        throw providerError;
      }

      setInkCost(provider.ink_cost || 0);
      setCertificateCost(provider.certificate_cost || 0);
      setCutCost(provider.cut_cost || 0);

      const { data: providerMedia, error: providerMediaError } = await supabase
        .from("provider_media")
        .select(`
          media_id,
          price,
          is_available,
          media (
            media_id,
            media_name,
            media_descr
          )
        `)
        .eq("provider_id", providerId);

      if (providerMediaError) {
        console.error("Error fetching provider media:", providerMediaError);
        throw providerMediaError;
      }

      const { data: allMedia, error: mediaError } = await supabase
        .from("media")
        .select("*");

      if (mediaError) {
        console.error("Error fetching all media:", mediaError);
        throw mediaError;
      }

      const combinedData = allMedia.map(media => {
        const offering = providerMedia?.find(pm => pm.media_id === media.media_id);
        return {
          ...media,
          price: offering?.price || 0,
          is_available: offering?.is_available ?? false,
          isOffered: !!offering
        };
      });

      setLocalMediaOfferings(combinedData);
      return combinedData;
    },
    enabled: isOpen && !!providerId,
  });

  const updateMediaMutation = useMutation({
    mutationFn: async (offerings: MediaOffering[]) => {
      console.log("Updating media offerings:", offerings);
      
      // Update provider costs
      const { error: providerError } = await supabase
        .from("print_service_providers")
        .update({ 
          ink_cost: inkCost,
          certificate_cost: certificateCost,
          cut_cost: cutCost
        })
        .eq("provider_id", providerId);

      if (providerError) throw providerError;

      // Update media offerings
      const { error } = await supabase
        .from("provider_media")
        .upsert(
          offerings.map(offering => ({
            provider_id: providerId,
            media_id: offering.media_id,
            price: offering.price,
            is_available: offering.is_available,
          }))
        );

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provider-media"] });
      toast({
        title: "Success",
        description: "Media offerings updated successfully",
      });
      onClose();
    },
    onError: (error) => {
      console.error("Error updating media offerings:", error);
      toast({
        title: "Error",
        description: "Failed to update media offerings",
        variant: "destructive",
      });
    },
  });

  const handlePriceChange = (mediaId: number, price: string) => {
    const numericPrice = parseFloat(price) || 0;
    setLocalMediaOfferings(prev =>
      prev.map(media =>
        media.media_id === mediaId
          ? { ...media, price: numericPrice }
          : media
      )
    );
  };

  const handleAvailabilityChange = (mediaId: number, isAvailable: boolean) => {
    setLocalMediaOfferings(prev =>
      prev.map(media =>
        media.media_id === mediaId
          ? { ...media, is_available: isAvailable }
          : media
      )
    );
  };

  const handleSave = () => {
    const offeringsToUpdate = localMediaOfferings.filter(
      media => media.price > 0 || media.is_available
    );
    updateMediaMutation.mutate(offeringsToUpdate);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Manage Media Offerings</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[calc(90vh-12rem)]">
          {isLoading ? (
            <div>Loading media offerings...</div>
          ) : (
            <MediaOfferingsTable
              mediaOfferings={localMediaOfferings}
              onPriceChange={handlePriceChange}
              onAvailabilityChange={handleAvailabilityChange}
            />
          )}
        </div>
        <MediaDialogFooter
          inkCost={inkCost}
          certificateCost={certificateCost}
          cutCost={cutCost}
          onInkCostChange={setInkCost}
          onCertificateCostChange={setCertificateCost}
          onCutCostChange={setCutCost}
          onSave={handleSave}
          isPending={updateMediaMutation.isPending}
        />
      </DialogContent>
    </Dialog>
  );
}
